import { Route } from './api.types'

/**
 * Per-workout detail queries and aggregation for the Apple Health sync: heart rate samples,
 * cycling power samples, and the GPS workout route, combined into the enriched ActivityEntry
 * shape (client-computed averages + a bounded route with per-point hr/pwr), mirroring how the
 * Garmin integration splits summaries and details server-side.
 */

/** A raw route point as returned by the native queryWorkoutRoutes bridge. */
export interface RawRoutePoint {
  lat: number
  lng: number
  alt?: number
  /** Epoch milliseconds. */
  timestamp: number
  /** Meters per second; absent when CLLocation reported an invalid (negative) speed. */
  spd?: number
}

/** A timestamped sample value (heart rate bpm, power watts). */
export interface TimedSample {
  /** Epoch milliseconds. */
  ts: number
  value: number
}

export interface WorkoutDetails {
  avgHeartRate: number | null
  avgPower: number | null
  elevationGain: number | null
  elevationLoss: number | null
  maxSpeed: number | null
  route: Route[] | null
}

// Upload size bound: a multi-hour workout records thousands of 1/sec points; ~1000 evenly
// spaced real points keep the payload small while staying visually indistinguishable.
export const ROUTE_POINT_CAP = 1000

// Altitude noise below this threshold (GPS jitter) must not accumulate into elevation gain.
const ELEVATION_THRESHOLD_M = 2

const HR_ATTACH_TOLERANCE_MS = 10_000
const PWR_ATTACH_TOLERANCE_MS = 5_000

// Workout activity types whose power series is worth querying (cycling power meters).
// cordova-plugin-health reports HealthKit cycling workouts as 'biking' (with variants like
// 'biking.mountain'); 'cycling' is the in-app recording flow's naming.
const isCyclingType = (activityType: string): boolean =>
  activityType === 'cycling' || activityType === 'biking' || !!activityType?.startsWith('biking.')

/** Rounded mean of the sample values, or null when there are none. */
export const computeAvgFromSamples = (samples: TimedSample[]): number | null => {
  if (!samples.length) return null
  const sum = samples.reduce((acc, sample) => acc + sample.value, 0)
  return Math.round(sum / samples.length)
}

/**
 * Total ascent/descent over the route altitudes with hysteresis: the reference altitude only
 * moves once the change exceeds the threshold, so meter-level GPS jitter doesn't accumulate.
 * Null when fewer than two points carry an altitude.
 */
export const computeElevationGainLoss = (
  points: RawRoutePoint[],
  thresholdM: number = ELEVATION_THRESHOLD_M,
): { gain: number; loss: number } | null => {
  const altitudes = points.filter((point) => typeof point.alt === 'number').map((point) => point.alt as number)
  if (altitudes.length < 2) return null
  let reference = altitudes[0]
  let gain = 0
  let loss = 0
  altitudes.forEach((alt) => {
    const delta = alt - reference
    if (delta >= thresholdM) {
      gain += delta
      reference = alt
    } else if (delta <= -thresholdM) {
      loss += -delta
      reference = alt
    }
  })
  return { gain: Math.round(gain), loss: Math.round(loss) }
}

/** Highest valid speed (m/s) over the route, or null when no point carries one. */
export const computeMaxSpeed = (points: RawRoutePoint[]): number | null => {
  const speeds = points.map((point) => point.spd).filter((spd): spd is number => typeof spd === 'number' && spd > 0)
  return speeds.length ? Math.max(...speeds) : null
}

/**
 * Bounds a route to `cap` points by picking evenly spaced REAL points (first and last always
 * kept). GPS coordinates are never averaged — an averaged point could sit off the road.
 */
export const downsampleRoute = <T>(points: T[], cap: number = ROUTE_POINT_CAP): T[] => {
  if (points.length <= cap) return points
  const step = (points.length - 1) / (cap - 1)
  const picked: T[] = []
  for (let i = 0; i < cap; i++) {
    picked.push(points[Math.round(i * step)])
  }
  return picked
}

/**
 * Attaches the nearest-in-time sample value to each route point (single pass over both sorted
 * arrays). Points with no sample within the tolerance stay untouched, so sensor dropouts become
 * gaps rather than stale values.
 */
export const attachNearestSample = (
  points: Route[],
  samples: TimedSample[],
  field: 'hr' | 'pwr',
  toleranceMs: number,
): Route[] => {
  if (!samples.length) return points
  const sorted = [...samples].sort((a, b) => a.ts - b.ts)
  let cursor = 0
  return points.map((point) => {
    while (
      cursor < sorted.length - 1 &&
      Math.abs(sorted[cursor + 1].ts - point.ts) <= Math.abs(sorted[cursor].ts - point.ts)
    ) {
      cursor++
    }
    const nearest = sorted[cursor]
    if (Math.abs(nearest.ts - point.ts) > toleranceMs) return point
    return { ...point, [field]: nearest.value }
  })
}

/** Converts raw bridge route points to upload Route points, dropping ones without a GPS fix. */
export const toUploadRoutePoints = (raw: RawRoutePoint[]): Route[] =>
  raw
    .filter((point) => typeof point.lat === 'number' && typeof point.lng === 'number' && !!point.timestamp)
    .map((point) => ({
      lat: point.lat,
      lng: point.lng,
      ts: point.timestamp,
      ...(typeof point.alt === 'number' ? { alt: point.alt } : {}),
      ...(typeof point.spd === 'number' && point.spd >= 0 ? { spd: point.spd } : {}),
    }))

const healthkit = (): any => (window as any).plugins?.healthkit

const queryWorkoutRoutes = (workoutId: string): Promise<RawRoutePoint[]> =>
  new Promise((resolve, reject) => healthkit().queryWorkoutRoutes(workoutId, resolve, reject))

const queryHeartRateSamples = (workoutId: string): Promise<TimedSample[]> =>
  new Promise((resolve, reject) =>
    healthkit().queryHeartRateDuringWorkout(
      workoutId,
      (samples: { bpm: number; timestamp: number }[]) =>
        resolve((samples || []).map((sample) => ({ ts: sample.timestamp, value: sample.bpm }))),
      reject,
    ),
  )

const queryCyclingPowerSamples = (startDate: Date, endDate: Date): Promise<TimedSample[]> =>
  new Promise((resolve, reject) =>
    healthkit().querySampleType(
      { sampleType: 'HKQuantityTypeIdentifierCyclingPower', unit: 'W', startDate, endDate, limit: 20000 },
      (samples: { startDate: string; quantity: number }[]) =>
        resolve(
          (samples || [])
            .filter((sample) => typeof sample.quantity === 'number')
            .map((sample) => ({ ts: new Date(sample.startDate).getTime(), value: sample.quantity })),
        ),
      reject,
    ),
  )

/**
 * Fetches and aggregates the details of one workout. Every sub-query is best-effort: a failure
 * (missing permission, unsupported type, no data) yields nulls for its metrics, never an error.
 * Power is only queried for cycling workouts; the route only when `includeRoute` (the company's
 * location-tracking flag) is set.
 */
export const fetchWorkoutDetails = async (
  workout: { id: string; startDate: Date; endDate: Date; activityType: string },
  includeRoute: boolean,
): Promise<WorkoutDetails> => {
  let hrSamples: TimedSample[] = []
  try {
    hrSamples = await queryHeartRateSamples(workout.id)
  } catch (e) {
    console.log(`AH details: HR query failed | ${workout.activityType} ${workout.id} |`, e)
    hrSamples = []
  }

  let pwrSamples: TimedSample[] = []
  if (isCyclingType(workout.activityType)) {
    try {
      pwrSamples = await queryCyclingPowerSamples(workout.startDate, workout.endDate)
    } catch (e) {
      console.log(`AH details: power query failed | ${workout.activityType} ${workout.id} |`, e)
      pwrSamples = []
    }
  }

  let rawRoute: RawRoutePoint[] = []
  if (includeRoute) {
    try {
      rawRoute = (await queryWorkoutRoutes(workout.id)) || []
    } catch (e) {
      console.log(`AH details: route query failed | ${workout.activityType} ${workout.id} |`, e)
      rawRoute = []
    }
  }

  console.log(
    `AH details: ${workout.activityType} ${workout.id} | hr samples: ${hrSamples.length}` +
      ` | pwr samples: ${pwrSamples.length} | route points: ${rawRoute.length} (includeRoute: ${includeRoute})`,
  )

  // Elevation and max speed come from the FULL route, before downsampling.
  const elevation = computeElevationGainLoss(rawRoute)
  const maxSpeed = computeMaxSpeed(rawRoute)

  let route: Route[] | null = null
  if (rawRoute.length) {
    let points = downsampleRoute(toUploadRoutePoints(rawRoute))
    points = attachNearestSample(points, hrSamples, 'hr', HR_ATTACH_TOLERANCE_MS)
    points = attachNearestSample(points, pwrSamples, 'pwr', PWR_ATTACH_TOLERANCE_MS)
    route = points.length ? points : null
  }

  return {
    avgHeartRate: computeAvgFromSamples(hrSamples),
    avgPower: computeAvgFromSamples(pwrSamples),
    elevationGain: elevation ? elevation.gain : null,
    elevationLoss: elevation ? elevation.loss : null,
    maxSpeed,
    route,
  }
}
